import { useModals } from 'almanac-ui/Modal'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { generatePath, useHistory } from 'react-router-dom'
import { reportError } from 'utils/error'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { DESIGN_PATH, FOLDER_PATH } from 'paths'
import { useDefaultFolder } from 'hooks/queries/projects/useDefaultFolder'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { showInternalServerErrorMessage } from 'common/ToastMessage/utils'
import { WizardProvider } from 'components/Wizards/Wizard/WizardContext'
import { ImagesProvider } from 'components/Wizards/SelectImages/ImagesProvider'
import { MultiStepWizard } from 'components/Wizards/MultiStepWizard/MultiStepWizard'
import { DesktopWizardProps, Values } from '../Types'
import { PropsWithChildren } from 'react'
import { useBrandKits } from 'hooks/queries/brandKits/useBrandKits'
import { useLastUsedLanguage } from 'hooks/queries/useLastUsedLanguage'
import { DEFAULT_LANGUAGE } from 'components/Wizards/Wizard/Form/LanguageInputField/LanguageInput'
import { useGenerateContent } from 'components/Wizards/Wizard/useGenerateContent'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { pick } from 'lodash'
import { useCreateDesignMutation } from '../hooks/useCreateDesignMutation'
import { AlmanacFileSummary } from 'types'
import { useIsScheduledPostsV3 } from 'hooks/isScheduledPostsV3'
import { TYPES_WITH_PREVIEW } from 'screens/Calendar/FullScreenPreview/Types'
import { DesignChannel } from 'components/NewFileModal/Types'
import dayjs from 'dayjs'
import { getEmptySnapshot } from 'components/VisualStudioPlayground/VisualEditor/utils/getEmptySnapshot'

export const DesktopWizard = ({
  children,
  isGeneratingDoc,
  onSuccess,
  ...props
}: PropsWithChildren<DesktopWizardProps>) => {
  const { schema, initialValues } = props
  const { closeAllModals, openModal } = useModals()
  const workspaceId = useCurrentWorkspaceId()
  const defaultFolder = useDefaultFolder()
  const { currentFolder } = useCurrentFolderStore()
  const { data: brandKits } = useBrandKits()
  const { data: lastUsedLanguage } = useLastUsedLanguage()
  const history = useHistory()
  const isScheduledPostsV3 = useIsScheduledPostsV3()
  const showPostPreview = schema.meta.channel && TYPES_WITH_PREVIEW.includes(schema.meta.channel as DesignChannel)

  const channel = schema.meta.channel

  const { generateContent } = useGenerateContent({
    generationType: channel as GenerationType | ChannelType,
    promptLibrary: 'default',
    onSuccess,
  })

  const handleGenerationStart = () => showToastMessage('Generating your post...', 'loading', undefined, 0)

  const handleSuccess = async ({
    design,
    otherAccounts,
  }: {
    design: any
    otherAccounts: Values['cross_post_accounts'] | undefined
  }) => {
    onSuccess?.(
      design as {
        almanac_file: Omit<AlmanacFileSummary, 'ancestors'>
      },
    )
    closeAllModals()

    if (isScheduledPostsV3 && showPostPreview) {
      const { FullScreenPreviewWithCarousel } = await import(
        'screens/Calendar/FullScreenPreview/FullScreenPreviewWithCarousel'
      )
      openModal(FullScreenPreviewWithCarousel, {
        id: design.id,
        channel: schema.meta.channel as DesignChannel,
        showCarousel: false,
        filters: {},
        width: design.width,
        onClose: () => closeAllModals(),
        startDate: dayjs().toISOString(),
        height: design.height,
      })
    } else {
      if (otherAccounts?.length) {
        history.push(
          generatePath(FOLDER_PATH, {
            folderSlug: design.parent?.seo_string as string,
          }),
        )
      } else {
        history.push(generatePath(DESIGN_PATH, { key: design.id }))
      }
    }
  }

  const { createDesign } = useCreateDesignMutation({
    workspaceId,
    channel,
    currentFolder,
    defaultFolder,
    onGenerationStart: handleGenerationStart,
    onSuccess: handleSuccess,
    onError: (err) => {
      reportError(err)
      showInternalServerErrorMessage()
    },
  })

  const handleSubmit = async (values: Values) => {
    if (isGeneratingDoc && !(isScheduledPostsV3 && showPostPreview)) {
      const docFields = {
        generation_type: channel as GenerationType | ChannelType,
        ...pick(
          values,
          'audience',
          'avoid',
          'brand_kit_id',
          'brand_voice_id',
          'caption_type',
          'filename',
          'include_emojis',
          'language',
          'links',
          'post_length',
          'project',
          'source_material_ids',
          'tone',
          'topic',
          'selected_images',
          'selected_videos',
        ),
      }
      return generateContent(docFields)
    } else {
      if (isScheduledPostsV3 && showPostPreview && isGeneratingDoc) {
        return createDesign({ ...values, content: getEmptySnapshot(values.width, values.height) })
      }
      return createDesign(values)
    }
  }

  return (
    <WizardProvider schema={schema}>
      <ImagesProvider>
        <MultiStepWizard.Root
          schema={schema}
          onSubmit={handleSubmit}
          initialValues={{
            project: currentFolder || defaultFolder,
            brand_kit_id: brandKits?.[brandKits.length - 1]?.id,
            language: lastUsedLanguage?.language || DEFAULT_LANGUAGE,
            ...initialValues,
          }}
          initialStep={props.initialStep}
          initialCompletedAll={props.initialCompletedAll}
        >
          {children}
        </MultiStepWizard.Root>
      </ImagesProvider>
    </WizardProvider>
  )
}
